import { AppContext, AppAction } from "helpers/context";
import { useContext, useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import Icon from "components/icon/icon";
import styles from "./climate-multiple-results.module.scss";
import { ReactComponent as IconTable } from "assets/icon_table_th.svg";
import { ReactComponent as MapAnalysisSvg } from "assets/map_analysis.svg";
import { ReactComponent as NotAnalysisSvg } from "assets/notAnalysis.svg";
import {
	IconButton, Paper, Table, Select,
	TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, Checkbox, Tooltip,
	MenuItem, LinearProgress
} from "@mui/material";
import TryAgain from "components/tryAgain/tryAgain";
import { formatKM, formatAmount, handleFilterLocations, toFixedTwo } from "helpers/helpers";
import BarChart from "components/results/chart-modal/bar-chart/bar-chart"
import { handleGetBarChartGroupData } from "helpers/groupBarChartData"

enum Tabs {
	SSP126 = "SSP1-2.6",
	SSP245 = "SSP2-4.5",
	SSP370 = "SSP3-7.0",
	SSP585 = "SSP5-8.5",
}

const BgColor = (num: any) => {
	if (num === "" || isNaN(num)) {
		if (num === "Outside" || num === "No Observation") {
			return "#f2f2f2"
		} else {
			return "grey"
		}
	} else if (num < 2) {
		return "rgb(181, 230, 162)";
	} else if (num >= 2 && num < 4) {
		return "rgb(218, 242, 208)";
	} else if (num >= 4 && num < 6) {
		return "rgb(255, 255, 204)";
	} else if (num >= 6 && num < 8) {
		return "rgb(255, 199, 206)";
	} else {
		return "rgb(204, 0, 0)";
	}
};

export default function ClimateMultipleResults() {
	const [context, dispatch] = useContext(AppContext);
	const [t] = useTranslation();
	const [activeLegend, setActiveLegend] = useState(0);
	const [riskValue, setRiskValue] = useState<string>(Tabs.SSP126);
	const [yearValue, setYearValue] = useState<string>("2030");
	const [chartOption, setChartOption] = useState<any>(undefined);
	const [chartHeight, setChartHeight] = useState(0);
	const [tableDatas, setTableDatas] = useState<any[]>([]);
	const [noData, setNoData] = useState<any[]>([]);
	const [sortData, setSortData] = useState<any[]>([]);
	const [rowCount, setRowCount] = useState(0);
	const [active, setActive] = useState("sortIndex");
	const [direction, setDirection] = useState<{ [key: string]: "asc" | "desc" }>({ sortIndex: "asc", value: "asc", share: "asc" });
	const noAnalysisLocationRef = useRef<any[]>([]);
	const [locationsTable, setLocationsTable] = useState<any[]>([])
	const unit = context.currentMultipleLocationsAnalysed?.aggregationUnit ?? "numberOfLocations";
	const isNumberOfLocations = unit === "numberOfLocations";

	const handleRetry = () => {
		dispatch({ type: AppAction.setTryAgainType, payload: "analyse" });
	}

	const handleChangeRisk = (e: any) => {
		handleLegendClick(0)
		setRiskValue(e.target.value);
	};

	const handleChangeYear = (e: any) => {
		handleLegendClick(0)
		setYearValue(e.target.value);
	};

	const handleGetLocationNum = (item: any) => {
		let num = sortData[activeLegend]?.filter((i: any) => i.checked && i.valueLabel === item.valueLabel).length
		return num === 0 ? "-" : num
	}

	const handleGetLocationUnit = (item: any) => {
		let num = sortData[activeLegend]?.reduce((count: number, i: any) => {
			if (i.checked && i.valueLabel === item.valueLabel) {
				count += Math.round(i[unit])
			}
			return count
		}, 0)
		return num === 0 ? "-" : formatAmount(num)
	}

	const handleGetLocationPercent = (item: any) => {
		if (isNumberOfLocations) {
			let num = sortData[activeLegend]?.filter((i: any) => i.checked && i.valueLabel === item.valueLabel).length
			let total = sortData[activeLegend]?.filter((i: any) => i.checked).length
			return num === 0 ? "-" : (toFixedTwo(num / (total + noAnalysisLocationRef.current.length) * 100) + "%")
		} else {
			let num = 0;
			let total = 0;
			sortData[activeLegend]?.forEach((i: any) => {
				if (i.checked) {
					total += Math.round(i[unit])
					if (i.valueLabel === item.valueLabel) {
						num += Math.round(i[unit])
					}
				}
			})
			noAnalysisLocationRef.current.forEach((i) => {
				total += Math.round(i[unit])
			})
			return num === 0 ? "-" : (toFixedTwo(num / total * 100) + "%")
		}
	}

	const getTotalText = () => {
		let num = sortData[activeLegend]?.filter((i: any) => i.checked).length || 0
		if (noAnalysisLocationRef.current.length > 0) {
			num += noAnalysisLocationRef.current.length
		}
		return num
	}

	const getUnitText = () => {
		let total = 0;
		[...(sortData[activeLegend] || []), ...noAnalysisLocationRef.current].forEach((i) => {
			i.checked && (total += Math.round(i[unit]))
		})
		return total === 0 ? "-" : formatAmount(total);
	}

	const handleGetNoAnalysisLocationPercent = () => {
		let num = noAnalysisLocationRef.current.length;
		let total = sortData[activeLegend]?.filter((i: any) => i.checked).length ?? 0
		return (toFixedTwo(num / (total + num) * 100) + "%")
	}

	const handleDeleteNoData = () => {
		context.currentMultipleLocations!.items = context.currentMultipleLocations!.items?.map((location: any) => {
			noAnalysisLocationRef.current.forEach(item => {
				let itemId = item.id.split("-").join("");
				let locationId = location.id.split("-").join("");
				if (itemId === locationId) {
					location.checked = false;
				}
			})
			return location
		})
		dispatch({
			type: AppAction.setCurrentMultipleLocations,
			payload: context.currentMultipleLocations,
		});
	}

	const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
		let pointIds = sortData[activeLegend]?.map((data: any) => data.pointId.split("-").join(""));

		context.currentMultipleLocations!.items = context.currentMultipleLocations!.items?.map((location: any) => {
			if (event.target.checked && pointIds.includes(location.id.split("-").join(""))) {
				location.checked = true;
			} else {
				location.checked = false;
			}
			return location
		})

		dispatch({
			type: AppAction.setCurrentMultipleLocations,
			payload: context.currentMultipleLocations,
		});
	}

	const toggleLocation = (evt: any, item: any) => {
		let pointIds = sortData[activeLegend]?.filter((data: any) => data.valueLabel === item.valueLabel)
			.map((data: any) => data.pointId.split("-").join(""));

		context.currentMultipleLocations!.items = context.currentMultipleLocations!.items?.map((location: any) => {
			if (pointIds.includes(location.id.split("-").join(""))) {
				location.checked = evt.target.checked;
			}
			return location
		})

		dispatch({
			type: AppAction.setCurrentMultipleLocations,
			payload: context.currentMultipleLocations,
		});
	}

	const handleSort = (property: string) => {
		if (!tableDatas.length) {
			return;
		}

		if (!property) {
			return;
		}

		setActive(property);
		direction[property] = direction[property] === "asc" ? "desc" : "asc";
		setDirection({ ...direction });

		handleSetSortData(JSON.parse(JSON.stringify(tableDatas)), property);
	};

	const handleSetSortData = (tableData: any, property = active, activeIndex = activeLegend) => {
		if (!tableData?.length) {
			setRowCount(0);
			setSortData([]);
			return
		};
		let sortArr = [...tableData];
		let key = property;
		if (key === "share") {
			if (isNumberOfLocations) {
				key = "value";
			} else {
				key = unit;
			}
		}
		sortArr[activeIndex].sort((a: any, b: any) => {
			if (!a || !b) {
				return 0;
			}

			let aValue = undefined;
			let bValue = undefined;
			if (key === "value") {
				if (a?.value) {
					aValue = sortArr[activeIndex].filter((item: any) => item.checked && a.valueLabel === item.valueLabel).length;
				} else {
					aValue = 0
				}
				if (b?.value) {
					bValue = sortArr[activeIndex].filter((item: any) => item.checked && b.valueLabel === item.valueLabel).length;
				} else {
					bValue = 0
				}
			} else if (key === unit) {
				if (a?.value) {
					aValue = sortArr[activeIndex].reduce((accumulator: number, currentItem: any) => {
						if (currentItem.checked && currentItem.valueLabel === a.valueLabel) {
							return accumulator + (Math.round(currentItem[key]));
						} else {
							return accumulator
						}
										// 这里需要让 total 做累计计算
					}, 0);
				} else {
					aValue = 0
				}
				if (b?.value) {
					bValue = sortArr[activeIndex].reduce((accumulator: number, currentItem: any) => {
						if (currentItem.checked && currentItem.valueLabel === b.valueLabel) {
							return accumulator + (Math.round(currentItem[key]) || 0);
						} else {
							return accumulator
						}
										// 这里需要让 total 做累计计算
					}, 0);
				} else {
					bValue = 0
				}
			} else {
				aValue = a?.[key];
				bValue = b?.[key];
			}

			if (aValue === undefined) {
				return direction[property] === "asc" ? -1 : 1;
			}
			if (bValue === undefined) {
				return direction[property] === "asc" ? 1 : -1;
			}
			if (aValue < bValue) {
				return direction[property] === "asc" ? 1 : -1;
			}
			if (aValue > bValue) {
				return direction[property] === "asc" ? -1 : 1;
			}
			return 0;
		});
		setRowCount(sortArr[activeIndex].filter((item: any) => item.checked).length);
		setSortData([...sortArr]);
	};

	const handleCheckedStatus = (item: any) => {
		let total = 0,
			num = 0;
		sortData[activeLegend]?.map((i: any) => {
			if (i.valueLabel == item.valueLabel) {
				total++;
				if (i.checked) {
					num++
				}
			}
		})
		return (num > 0) && (num === total);
	}

	const handleIndeterminateStatus = (item: any) => {
		let total = 0,
			num = 0;
		sortData[activeLegend]?.map((i: any) => {
			if (i.valueLabel == item.valueLabel) {
				total++;
				if (i.checked) {
					num++
				}
			}
		})
		return (num > 0) && (num < total);
	}

	const handleLegendClick = (index: number) => {
		setActiveLegend(index);
	};

	const handleSetOptions = (tableData: any, activIndex = activeLegend) => {
		const { Ynum, option } = handleGetBarChartGroupData({
			tableData,
			activIndex,
			noAnalysisLocationRef,
			analysedLegends: context.hazardMultipleAnalysedLegends,
			analysedLocationSet: context.currentMultipleLocationsAnalysed ?? context.currentMultipleLocations,
			t
		})
		setChartHeight(Ynum);
		setChartOption(option);
	};

	const handleSwitchValueLabel = (value: string) => {
		if (value === "Outside" || value === "No Data" || value === "No_Data" || value === "No Observation" || value === "Negligible") return value;
		switch (true) {
			case parseInt(value) < 2:
				return "negligible";
			case parseInt(value) >= 2 && parseInt(value) < 4:
				return "minor";
			case parseInt(value) >= 4 && parseInt(value) < 6:
				return "moderate";
			case parseInt(value) >= 6 && parseInt(value) < 8:
				return "significant";
			case parseInt(value) >= 8:
				return "severe";
			default:
				return value;
		}
	};

	const showInfo = () => {
		dispatch({ type: AppAction.setShowLocationAnalysisDetails, payload: true });
	};

	const getData = () => {
		if (context.climateMultipleAnalysisResult && context.climateMultipleAnalysedLegends && context.climateMultipleAnalysedLegends.length > 0
			&& context.climateMultipleAnalysisStatus !== "error" && context.climateMultipleAnalysisStatus !== "running") {

			if (context.climateMultipleAnalysisResult.length === 0 || locationsTable?.length === 0) {
				if (locationsTable) {
					let arr = locationsTable.filter((location: any) => location.checked)
					if (arr.length === 0) {
						let arr = context.climateMultipleAnalysedLegends.map((item: any) => {
							item.layerId = item.id;
							return item
						});
						setNoData(arr);
						setSortData([]);
						setRowCount(0);
						handleSetOptions([], 0);
						setTableDatas([]);
						setActiveLegend(0);
					} else {
						setNoData([]);
						setSortData([]);
						setRowCount(noAnalysisLocationRef.current.length);
						handleSetOptions([], 0);
						setTableDatas([]);
						setActiveLegend(0);
					}
				}
				return;
			}
			let arr: any = [];
			let noAnalysisLocationData = [...noAnalysisLocationRef.current]
			context.climateMultipleAnalysedLegends.forEach((legend: any, index: number) => {
				arr.push([]);
				context.climateMultipleAnalysisResult!.forEach((item: any) => {
					if (noAnalysisLocationData.length) {
						noAnalysisLocationData = noAnalysisLocationData.filter((location: any) => {
							let locationId = location.id.split("-").join("");
							let itemId = item.id.split("-").join("");
							let isFind = false;
							context.climateMultipleAnalysedLegends?.forEach((legend: any) => {
								isFind = item.climateRiskScores?.find((itemScores: any) => legend.id === itemScores.index);
							});
							if (locationId === itemId && isFind) {
								return false
							}
							return true
						})
					}
					let findTableLocations = locationsTable.find((i: any) => i.id.split("-").join("") === item.id.split("-").join(""));
					if (!findTableLocations) return
					if (item.checked === undefined) {
						item.checked = true;
					}
					let id = item.id.split("-").join("")
					let scenario = item.climateRiskScores.find((s: any) => s.index === legend.id);
					let itemUnit = "";
					if (!isNumberOfLocations) {
						let find = context.currentMultipleLocationsAnalysed?.items?.find((i: any) => i.id.split("-").join("") === item.id.split("-").join(""));
						if (find) {
							itemUnit = find[unit];
						}
					}
					if (scenario) {
						let scenarioData = scenario.scenarios.find((s: any) => s.name === riskValue);
						if (scenarioData) {
							let band = scenarioData.bandGroups[0].bands.find((band: any) => band.name === yearValue);
							if (band) {
								arr[index].push({
									value: 1,
									valueLabel: band.value === "" || band.value === "NaN" ? "" : handleSwitchValueLabel(band.value),
									sortIndex: band.value === "" || isNaN(band.value) ? 0 : band.value,
									layerId: legend.id,
									itemStyle: {
										color: BgColor(band.value),
										opacity: band.value === "" || isNaN(band.value) ? 0.5 : 1,
									},
									pointId: id,
									checked: item.checked,
									[unit]: itemUnit,
								});
							}
						} else {
							arr[index].push({
								value: 1,
								valueLabel: "",
								sortIndex: 0,
								layerId: legend.id,
								itemStyle: {
									color: "grey",
									opacity: 0.5,
								},
								pointId: id,
								checked: item.checked,
								[unit]: itemUnit,
							});
						}
					}
				});
			});

			noAnalysisLocationRef.current = noAnalysisLocationData;
			let data: any = arr.map((item: any) => {
				return item.sort((a: any, b: any) => {
					return b.sortIndex - a.sortIndex;
				});
			});

			let tableData: any = [],
				noData: any = [];

			data.forEach((dataItem: any) => {
				const valueMap: any = {};
				if (dataItem && dataItem.length > 0) {
					dataItem.forEach((item: any) => {
						const label = item.valueLabel;
						if (valueMap[label] === undefined) {
							valueMap[label] = 1; // First occurrence
							item.value = dataItem.filter((i: any) => i.valueLabel === label).length; // Count total occurrences
						} else {
							item.value = 0; // Subsequent occurrences
						}
					});

					if (
						(dataItem[0].valueLabel === "Outside" || dataItem[0].valueLabel === "No Data" || dataItem[0].valueLabel === "No_Data" || dataItem[0].valueLabel === "No Observation" || dataItem[0].valueLabel === "" || dataItem[0].valueLabel === "Negligible") &&
						dataItem[0].value === dataItem.length
					) {
						noData.push(dataItem[0]);
					} else {
						tableData.push(dataItem);
					}
				}
			});

			setTableDatas(tableData);
			setNoData(noData);
			handleSetSortData(JSON.parse(JSON.stringify(tableData)));
			handleSetOptions(JSON.parse(JSON.stringify(tableData)));
		}
	};

	useEffect(() => {
		if (locationsTable && context.climateMultipleAnalysisResult) {
			let climateMultipleAnalysisResult: any[] = [...context.climateMultipleAnalysisResult];
			const noData = locationsTable.filter((location: any) => {
				let is = true;
				climateMultipleAnalysisResult.forEach((item: any) => {
					let itemId = item.id.split("-").join("");
					let locationId = location.id.split("-").join("");
					let isFind = false;
					context.climateMultipleAnalysedLegends?.forEach((legend: any) => {
						isFind = item.climateRiskScores?.find((itemScores: any) => legend.id === itemScores.index);
					});
					if (itemId === locationId && isFind) {
						item.checked = location.checked;
						is = false;
					}
				});
				return is && location.checked
			})

			noAnalysisLocationRef.current = noData;
			dispatch({
				type: AppAction.setClimateMultipleAnalysisResult,
				payload: climateMultipleAnalysisResult,
			});
		} else {
			getData();
		}
	}, [locationsTable])

	useEffect(() => {
		getData();
	}, [context.climateMultipleAnalysedLegends, context.climateMultipleAnalysisResult]);

	useEffect(() => {
		setActiveLegend(0);
	}, [context.climateMultipleAnalysisStatus])

	useEffect(() => {
		let data = handleFilterLocations({
			data: context.currentMultipleLocations?.items || [],
			unit: context.currentMultipleLocations?.aggregationUnit ?? "numberOfLocations",
			multipleLocationsSearch: context.multipleLocationsSearch,
			locationsFilterInfo: context.locationsFilterInfo,
		})
		setLocationsTable(data)
	}, [
		context.currentMultipleLocations?.items,
		context.multipleLocationsSearch,
		context.locationsFilterInfo,
		context.currentMultipleLocations?.aggregationUnit,
		riskValue,
		yearValue
	])

	if (context.climateMultipleAnalysisStatus && context.climateMultipleAnalysisStatus === "error") {
		return (<TryAgain handleRetry={handleRetry} />);
	}

	if (!context.climateMultipleAnalysedLegends || context.climateMultipleAnalysedLegends.length === 0) {
		return (
			<div className="h-full flex flex-col items-center justify-center">
				<NotAnalysisSvg className="w-[60px] h-[60px] mx-auto mb-4" />
				<div style={{ color: "var(--gray-text)" }}>{t("single.notMultipleAnalysis")}</div>
			</div>
		);
	}

	if (context.climateMultipleAnalysisStatus && context.climateMultipleAnalysisStatus === "running") {
		return (
			<div className="h-full flex flex-col items-center justify-center">
				<LinearProgress
					color="success"
					style={{ width: "40%", marginBottom: "10px" }}
				/>
				<div>{t("multiple.climateAlalyseLoadingContent")}</div>
			</div>
		);
	}

	return (
		<>
			<div className={styles.locationInfo}>
				<div className="flex justify-between items-center">
					<span className="text-[#000000de] text-[13px] font-medium">{t("multiple.overview")}</span>
					<div className="flex items-center">
						<div className="mt-[3px] flex items-center">
							<Tooltip placement="top" title={t("results.resultsFor")}>
								<span>{t("results.resultsFor")}</span>
							</Tooltip>
							<MapAnalysisSvg className="w-[24px] h-[24px] fill-[#0009] mx-[5px] mt-[-5px]" />
							<Tooltip placement="top" title={`${(sortData[activeLegend]?.filter((i: any) => i.checked).length ?? 0)
								+ " / " + locationsTable.filter(v => v.checked).length}${t("results.locations")}`}>
								<span>
									{sortData[activeLegend]?.filter((i: any) => i.checked).length ?? 0}
									{" / "}
									{locationsTable.filter(v => v.checked).length}
									{t("results.locations")}
								</span>
							</Tooltip>
						</div>
						<Icon
							icon="info"
							type="outlined"
							onClick={showInfo}
						></Icon>
					</div>
				</div>
			</div>
			<div className="my-[10px]" style={{ marginRight: "0.75rem" }}>
				<span>{t("multiple.pathway")}: </span>
				<Select
					value={riskValue}
					className={`${styles.select} w-[100px] mr-[10px]`}
					onChange={handleChangeRisk}
					color="success"
				>
					<MenuItem value="SSP1-2.6">SSP 1-2.6 </MenuItem>
					<MenuItem value="SSP2-4.5">SSP 2-4.5</MenuItem>
					<MenuItem value="SSP3-7.0">SSP 3-7.0</MenuItem>
					<MenuItem value="SSP5-8.5">SSP 5-8.5</MenuItem>
				</Select>
				<span> {t("multiple.year")}: </span>
				<Select
					value={yearValue}
					className={`${styles.select} w-[78px]`}
					onChange={handleChangeYear}
					color="success"
				>
					<MenuItem value="2030">2030</MenuItem>
					<MenuItem value="2035">2035</MenuItem>
					<MenuItem value="2040">2040</MenuItem>
					<MenuItem value="2045">2045</MenuItem>
					<MenuItem value="2050">2050</MenuItem>
					<MenuItem value="2055">2055</MenuItem>
					<MenuItem value="2060">2060</MenuItem>
					<MenuItem value="2070">2070</MenuItem>
					<MenuItem value="2085">2085</MenuItem>
				</Select>
			</div>
			<div className={styles.resultsMultiple}>
				<div>
					<BarChart
						tableData={tableDatas}
						activeLegend={activeLegend}
						noAnalysisLocationRef={noAnalysisLocationRef}
						noData={noData}
						chartHeight={chartHeight}
						chartOption={chartOption}
						analysedLegends={context.hazardMultipleAnalysedLegends}
						handleLegendClick={handleLegendClick}
					/>
					{(tableDatas[activeLegend] || noData.length > 0 || noAnalysisLocationRef.current.length > 0) && (
						<div style={{ marginRight: "0.75rem" }}>
							<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingRight: "10px" }}>
								<p className="m-2 font-bold">{t(`layerId.${tableDatas[activeLegend] ?
									tableDatas[activeLegend][0].layerId :
									(noData[0] ?
										noData[0].layerId
										:
										context.climateMultipleAnalysedLegends && context.climateMultipleAnalysedLegends[activeLegend].id)
									}`)}</p>
								{/* <p className="my-3 font-medium">{t(`layerId.${tableData[activeLegend] ? tableData[activeLegend][0].layerId : noData[0].layerId}`)}</p> */}
								<div style={{ padding: "6px 10px", borderRadius: "5px", backgroundColor: "var(--green-hover)", boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.15)" }}>
									<IconTable />
								</div>
							</div>
							<Paper
								sx={{ width: "100%", boxShadow: "none" }}
								className={styles.table}
							>
								<TableContainer sx={{ height: "100%" }}>
									<Table
										stickyHeader
										aria-label="sticky table"
										size="small"
										style={{
											tableLayout: "fixed",
											width: "100%",
											minWidth: "100%"
										}}
									>
										<TableHead>
											<TableRow
												className={styles.row}
												sx={{
													"& th": {
														fontSize: "12px",
														color: "#000000de"
													}
												}}
											>
												<TableCell
													style={{ padding: 0, width: 44 }}
													align="center"
												>
													<Checkbox
														color="success"
														indeterminate={(rowCount > 0 || noAnalysisLocationRef.current.length > 0) && (sortData[activeLegend]?.length ?? 0) !== rowCount}
														checked={rowCount > 0 && (sortData[activeLegend]?.length ?? 0) === rowCount}
														onChange={handleSelectAllClick}
														inputProps={{
															"aria-label": "select all desserts",
														}}
													/>
												</TableCell>
												<TableCell align="left" style={{ width: "105px", padding: "8px" }}>
													<TableSortLabel
														active={active === "sortIndex"}
														onClick={() => handleSort("sortIndex")}
														direction={direction.sortIndex}
													>
														{context.language === "en" ? "Score in " : ""}
														{yearValue}
													</TableSortLabel>
												</TableCell>
												<TableCell align="right" style={{ width: "90px", padding: "8px" }}>
													<TableSortLabel
														active={active === "value"}
														onClick={() => handleSort("value")}
														direction={direction.value}
													>
														{t("locations.table.locations")}
													</TableSortLabel>
												</TableCell>
												{!isNumberOfLocations && (<TableCell align="right" style={{ width: "120px", padding: "8px" }}>
													<TableSortLabel
														active={active === "currency"}
														onClick={() => handleSort("currency")}
														direction={direction["currency"]}
													>
														{t("locations.add.currency")}
													</TableSortLabel>
												</TableCell>)}
												{!isNumberOfLocations && (<TableCell align="right" style={{ width: "120px", padding: "8px" }}>
													<TableSortLabel
														active={active === unit}
														onClick={() => handleSort(unit)}
														direction={direction[unit]}
													>
														{t("locations." + unit)}
													</TableSortLabel>
												</TableCell>)}
												<TableCell align="right" style={{ width: "85px", padding: "8px 16px 8px 8px" }}>
													<TableSortLabel
														active={active === "share"}
														onClick={() => handleSort("share")}
														direction={direction.share}
													>
														{t("locations.table.share")}
													</TableSortLabel>
												</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{sortData[activeLegend]?.map(
												(item: any, index: number) =>
													item.value > 0 && (
														<TableRow
															key={index}
															sx={{
																"& td": {
																	fontSize: "13px",
																	color: "#000000de"
																}
															}}
														>
															<TableCell
																style={{ padding: 0 }}
																align="center"
															>
																<Checkbox
																	color="success"
																	indeterminate={handleIndeterminateStatus(item)}
																	checked={handleCheckedStatus(item)}
																	onClick={(evt: any) => toggleLocation(evt, item)}
																/>
															</TableCell>
															<TableCell align="left" style={{ padding: "8px" }}>
																<div style={{ display: "flex", alignItems: "center" }}>
																	<span
																		style={{
																			flexShrink: 0,
																			width: "16px",
																			height: "16px",
																			marginRight: "5px",
																			display: "inline-block",
																			backgroundColor: item.itemStyle.color,
																			opacity: item.itemStyle.opacity,
																		}}
																	></span>
																	{item.valueLabel ? t("legend." + item.valueLabel) : ""}
																</div>
															</TableCell>
															<TableCell align="right" style={{ padding: "8px" }}>{handleGetLocationNum(item)}</TableCell>
															{!isNumberOfLocations && (<TableCell align="right" style={{ padding: "8px" }}>{context.currentMultipleLocations?.currency}</TableCell>)}
															{!isNumberOfLocations && (<TableCell align="right" style={{ padding: "8px" }}>{handleGetLocationUnit(item)}</TableCell>)}
															<TableCell align="right" style={{ padding: "8px 16px 8px 8px" }}>{handleGetLocationPercent(item)}</TableCell>
														</TableRow>
													)
											)}
											{noAnalysisLocationRef.current.length > 0 && (
												<TableRow
													sx={{
														"& td": {
															fontSize: "13px",
															color: "#000000de"
														}
													}}
												>
													<TableCell
														style={{ padding: 0 }}
														align="center"
													>
														<Checkbox
															color="success"
															checked={true}
															onClick={handleDeleteNoData}
														/>
													</TableCell>
													<TableCell align="left" style={{ padding: "8px" }}>
														<div style={{ display: "flex", alignItems: "center" }}>
															<span
																style={{
																	flexShrink: 0,
																	width: "16px",
																	height: "16px",
																	boxSizing: "border-box",
																	marginRight: "5px",
																	display: "inline-block",
																	backgroundColor: "rgb(242, 242, 242)",
																	border: "2.5px dotted #959595",
																}}
															></span>
															{t("legend.Not Analyzed")}
														</div>
													</TableCell>
													<TableCell align="right" style={{ padding: "8px" }}>{noAnalysisLocationRef.current.length}</TableCell>
													{!isNumberOfLocations && (<TableCell align="right" style={{ padding: "8px" }}>{context.currentMultipleLocations?.currency}</TableCell>)}
													{!isNumberOfLocations &&
														<TableCell align="right" style={{ padding: "8px" }}>{
															formatAmount(noAnalysisLocationRef.current.reduce((count: number, i: any) => count += Math.round(i[unit]), 0)) || "-"
														}</TableCell>}
													<TableCell align="right" style={{ padding: "8px 16px 8px 8px" }}>{
														handleGetNoAnalysisLocationPercent()
													}</TableCell>
												</TableRow>
											)}
											<TableRow
												style={{ fontWeight: "bold", height: "50px" }}
												sx={{
													"& td": {
														fontSize: "13px",
														color: "#000000de"
													}
												}}
											>
												<TableCell style={{ padding: "8px" }} colSpan={2}>{t("multiple.total")}</TableCell>
												<TableCell align="right" style={{ padding: "8px" }}>
													{getTotalText()}
												</TableCell>
												{!isNumberOfLocations && (<TableCell align="right" style={{ padding: "8px" }}>{context.currentMultipleLocations?.currency}</TableCell>)}
												{!isNumberOfLocations && <TableCell align="right" style={{ padding: "8px" }}>
													{getUnitText()}
												</TableCell>}
												<TableCell align="right" style={{ padding: "8px 16px 8px 8px" }}>100%</TableCell>
											</TableRow>
										</TableBody>
									</Table>
								</TableContainer>
							</Paper>
						</div>
					)}
				</div>
			</div>
		</>
	);
}

